<template>
  <rbz-card-collapsible :is-collapsed="false" data-qa="global-filter-entries-card">
    <template #title>
      <div class="card-title">
        <div v-if="editable">
          <rbz-dropdown-menu trigger-button-icon="IconMoreVertical" ref="dropdownMenu">
            <template #dropdownMenuContent>
              <menu-list-item
                :title="$t('globalFilter.configPage.ruleCard.clearSectionContent')"
                :disabled="isEntriesEmpty"
                classes="remove-all-entries-button remove-controls dropdown-item ignore-fix-focus"
                data-qa="remove-all-entries-button"
                @item-click="
                  removeAllEntries();
                  $refs.dropdownMenu.closeMenu();
                "
              />
              <template v-if="deletable">
                <LayoutDividerLine color="light-gray" />
                <menu-list-item
                  :title="$t('globalFilter.configPage.ruleCard.removeSection')"
                  :disabled="isEntriesEmpty"
                  classes="remove-section-button remove-controls dropdown-item ignore-fix-focus"
                  data-qa="remove-section-button"
                  @item-click="
                    emitRemoveSection();
                    $refs.dropdownMenu.closeMenu();
                  "
                />
              </template>
            </template>
          </rbz-dropdown-menu>
        </div>
        <span class="entries-count">
          {{ cardTitle }}
        </span>
        <rbz-form-button
          v-if="editable"
          classes="add-section-button"
          data-qa="add-section-button"
          variant="secondary"
          :label="$t('globalFilter.configPage.ruleCard.newSection')"
          icon="IconAdd"
          @button-click="addSection()"
        />
        <rbz-form-button
          classes="rule-relation-toggle"
          data-qa="rule-relation-toggle-button"
          variant="secondary"
          :label="
            $t('globalFilter.configPage.ruleCard.relation', { relation: localRule.value.relation })
          "
          :disabled="ruleContainsSameCategoryItems() || !editable"
          @button-click="toggleRuleRelation()"
        />
      </div>
    </template>
    <template #content>
      <div class="data-wrapper">
        <template v-if="isRecursive">
          <div
            class="sections-wrapper"
            v-for="(entry, entryIndex) in localRule.value.entries"
            :key="entry.id"
          >
            <global-filter-entries-relation-list
              v-model:rule="localRule.value.entries[entryIndex]"
              @update:rule="ruleUpdated($event, entryIndex)"
              @invalid="emitValidity($event)"
              @remove-section="removeSection(entryIndex)"
              :editable="editable"
              :deletable="true"
            />
          </div>
        </template>
        <template v-else>
          <rbz-table
            :columns="columns"
            :table-data="tableFormattedData"
            :row-buttons="rowButtons"
            :row-classes="
              (entry) => {
                return isEntryDuplicate(entry.category, entry.match) ? 'duplicate-entry' : '';
              }
            "
            :show-table-results="false"
            :show-download-button="false"
            :show-filter-button="false"
            :show-new-default-data="defaultNewEntry"
            :show-top-navbar="editable"
            show-new-as-row
            :new-button-label="$t('globalFilter.configPage.ruleCard.newEntry.buttonLabel')"
            ref="entriesTable"
            @edit-row-button-clicked="setEditRowEntryData($event)"
            @delete-row-button-clicked="removeEntry($event)"
            class="entries-table"
            data-qa="entries-table"
          >
            <template #newRowOverride="rowProps">
              <td>
                <rbz-dropdown
                  :items="Object.entries(listEntryTypes)"
                  v-model:value="rowProps.data.category"
                  @update:value="resetNewEntryValues(rowProps.data)"
                />
              </td>
              <td>
                <template v-if="['country'].includes(rowProps.data.category)">
                  <rbz-dropdown
                    :items="countries"
                    :value="rowProps.data.match"
                    @update:value="setNewEntryCountryDropdownMatch(rowProps, $event)"
                    type="single-select"
                    :virtual-scroll="true"
                    classes="new-entry-dropdown"
                    data-qa="new-entry-dropdown"
                  />
                </template>
                <template v-else-if="['asn'].includes(rowProps.data.category)">
                  <rbz-dropdown
                    :items="asns"
                    :value="rowProps.data.match"
                    @update:value="setNewEntryAsnDropdownMatch(rowProps, $event)"
                    type="single-select"
                    :virtual-scroll="true"
                    classes="new-entry-dropdown"
                    data-qa="new-entry-dropdown"
                  />
                </template>
                <template v-else-if="isCategorySimpleInput(rowProps.data.category)">
                  <div class="new-entry-match">
                    <rbz-form-input
                      name="new-entry-name"
                      classes="new-entry-name-input"
                      :placeholder="$t('globalFilter.configPage.ruleCard.newEntry.namePlaceholder')"
                      ref="newEntryMatchName"
                      data-qa="new-entry-name-input"
                      :is-invalid="!!newEntryMatchNameError(rowProps.data)"
                      :error-message-text="newEntryMatchNameError(rowProps.data)"
                      v-model:inputValue="rowProps.data.matchName"
                    />
                    <rbz-form-input
                      name="new-entry-value"
                      classes="new-entry-value-input"
                      :placeholder="
                        $t('globalFilter.configPage.ruleCard.newEntry.valuePlaceholder')
                      "
                      ref="newEntryMatchValue"
                      data-qa="new-entry-value-input"
                      :is-invalid="!!newEntryMatchValueError(rowProps.data)"
                      :error-message-text="newEntryMatchValueError(rowProps.data)"
                      v-model:inputValue="rowProps.data.matchValue"
                    />
                  </div>
                </template>
                <template v-else>
                  <rbz-form-textarea
                    v-if="rowProps.mode === 'new'"
                    name="new-entry-textarea"
                    ref="newEntryMatch"
                    data-qa="new-entry-value-textarea"
                    v-model:inputValue="rowProps.data.match"
                    :is-invalid="!!newEntryMatchError(rowProps.data)"
                    :error-message-text="newEntryMatchError(rowProps.data)"
                    :placeholder="
                      $t('globalFilter.configPage.ruleCard.newEntry.textareaPlaceholder')
                    "
                    classes="new-entry-textarea"
                    rows="3"
                  />
                  <rbz-form-input
                    v-else
                    name="new-entry-value"
                    classes="new-entry-value-input"
                    :placeholder="$t('globalFilter.configPage.ruleCard.newEntry.valuePlaceholder')"
                    ref="newEntryMatchValue"
                    data-qa="new-entry-value-input"
                    :is-invalid="!!newEntryMatchError(rowProps.data)"
                    :error-message-text="newEntryMatchError(rowProps.data)"
                    v-model:inputValue="rowProps.data.match"
                  />
                </template>
              </td>
              <td>
                <rbz-form-input
                  name="new-entry-annotation"
                  classes="new-entry-annotation-input"
                  :placeholder="
                    $t('globalFilter.configPage.ruleCard.newEntry.annotationPlaceholder')
                  "
                  ref="newEntryAnnotation"
                  :is-invalid="!!newEntryAnnotationError(rowProps.data)"
                  :error-message-text="newEntryAnnotationError(rowProps.data)"
                  v-model:inputValue="rowProps.data.annotation"
                />
              </td>
            </template>
            <template #newRowOverrideConfirmationButtons="rowProps">
              <td class="row-buttons-cell">
                <form-confirmation
                  @confirm="
                    rowProps.mode === 'new' ? addEntry(rowProps.data) : editEntry(rowProps.data)
                  "
                  :is-invalid="!!newEntryError(rowProps.data)"
                  @cancel="
                    rowProps.mode === 'new' ? cancelAddEntry() : cancelEditEntry(rowProps.data.id)
                  "
                />
              </td>
            </template>
          </rbz-table>
        </template>
      </div>
    </template>
  </rbz-card-collapsible>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import {
  EntriesCategory,
  GenericObject,
  GlobalFilterRuleWithIds,
  GlobalFilterRuleEntry,
  GlobalFilterRuleEntryWithIds,
  GlobalFilterRuleSectionWithIds,
  Relation,
} from '@/types';
import _ from 'lodash';
import RbzFormButton from './RbzFormButton.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzFormTextarea from '@/components/RbzFormTextarea.vue';
import RbzDropdown from '@/components/RbzDropdown.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzCardCollapsible from 'ui-component-library/rbz/components/RbzCardCollapsible.vue';
import FormConfirmation from 'ui-component-library/rbz/components/FormConfirmation.vue';
import Utils from 'ui-component-library/helpers/Utils';
import { mapStores } from 'pinia';
import { useDatasetsStore } from '@/store/DatasetsStore';
import RbzDropdownMenu from '@/components/RbzDropdownMenu.vue';
import DatasetsUtils from '@/helpers/DatasetsUtils';
import ipaddr from 'ipaddr.js';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';
import MenuListItem from 'ui-component-library/rbz/components/MenuListItem.vue';

type Entry = {
  id?: string;
  category: EntriesCategory | 'plugins.jwt';
  match: string;
  annotation: string;
  matchName?: string;
  matchValue?: string;
};

export default defineComponent({
  name: 'GlobalFilterEntriesRelationList',
  components: {
    MenuListItem,
    RbzDropdownMenu,
    FormConfirmation,
    RbzCardCollapsible,
    RbzTable,
    RbzFormButton,
    RbzDropdown,
    RbzFormInput,
    RbzFormTextarea,
    LayoutDividerLine,
  },
  props: {
    rule: {
      type: Object as PropType<GlobalFilterRuleSectionWithIds | GlobalFilterRuleEntryWithIds>,
      default: () => {
        return {
          relation: 'OR',
          entries: [] as GlobalFilterRuleWithIds[],
        };
      },
      validator: (value: GlobalFilterRuleSectionWithIds) => {
        const isSectionInvalidStructure = (section: GlobalFilterRuleSectionWithIds): boolean => {
          const isSectionRelationInvalid: boolean = !['OR', 'AND'].includes(
            section?.value?.relation?.toUpperCase()
          );
          const isSectionEntriesInvalid: boolean = _.some(section.value.entries, (entry) => {
            const isEntryTypeSection = !Array.isArray(entry.value);
            if (isEntryTypeSection) {
              return isSectionInvalidStructure(entry as GlobalFilterRuleSectionWithIds);
            } else {
              const arrayLength = (entry as GlobalFilterRuleEntryWithIds).value?.length;
              return arrayLength !== 3 && arrayLength !== 2;
            }
          });
          return isSectionRelationInvalid || isSectionEntriesInvalid;
        };
        return !isSectionInvalidStructure(value);
      },
    },
    editable: Boolean,
    deletable: Boolean,
  },
  data() {
    const listEntryTypes: GenericObject = {
      args: this.$t('keyOption.argument'),
      asn: this.$t('keyOption.asn'),
      authority: this.$t('keyOption.authority'),
      cookies: this.$t('keyOption.cookie'),
      country: this.$t('keyOption.country'),
      headers: this.$t('keyOption.header'),
      ip: this.$t('keyOption.ip'),
      method: this.$t('keyOption.method'),
      organization: this.$t('keyOption.organization'),
      path: this.$t('keyOption.path'),
      securitypolicyentryname: this.$t('keyOption.securityPolicyEntryName'),
      'plugins.jwt': this.$t('keyOption.plugin.jwt'),
      query: this.$t('keyOption.query'),
      region: this.$t('keyOption.region'),
      securitypolicyname: this.$t('keyOption.securityPolicyName'),
      subregion: this.$t('keyOption.subregion'),
      tag: this.$t('keyOption.tag'),
      uri: this.$t('keyOption.uri'),
    };

    return {
      columns: [
        {
          id: 'category',
          title: this.$t('globalFilter.configPage.ruleCard.category'),
          fieldNames: ['category'],
          displayFunction: (entry) => {
            if (entry.category === 'plugins') {
              const pluginType = entry.match[0].split('.')[0];
              return listEntryTypes[`plugins.${pluginType}`];
            }
            return listEntryTypes[entry.category];
          },
          classes: 'width-240px',
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'match',
          title: this.$t('globalFilter.configPage.ruleCard.match'),
          fieldNames: ['match'],
          displayFunction: (entry) => {
            return this.dualEntryMatch(entry.category, entry.match);
          },
          cellContentClasses: 'ellipsis',
        },
        {
          id: 'annotation',
          title: this.$t('globalFilter.configPage.ruleCard.annotation'),
          fieldNames: ['annotation'],
          classes: 'width-250px',
          cellContentClasses: 'ellipsis',
          tooltipFunctionAlwaysDisplayed: false,
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'edit',
          icon: 'IconEdit',
          editInline: true,
          disabledCallback: () => {
            return !this.editable;
          },
          tooltipCallback: () => {
            if (!this.editable) {
              return this.$t('globalFilter.configPage.ruleCard.editDisabled');
            }
            return this.$t('common.operations.edit.tableTooltip');
          },
        },
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: () => {
            return !this.editable;
          },
          tooltipCallback: () => {
            if (!this.editable) {
              return this.$t('globalFilter.configPage.ruleCard.editDisabled');
            }
            return this.$t('common.operations.delete.tableTooltip');
          },
        },
      ] as RbzTableRowButton[],
      listEntryTypes: listEntryTypes,
      // newEntryCategory - start with most common category - IP
      defaultNewEntry: {
        category: 'ip' as Entry['category'],
        match: '',
        matchName: '',
        matchValue: '',
        annotation: '',
      },
      duplicatedEntries: [] as [EntriesCategory, string | string[]][],
      countries: [] as [string, string][],
      asns: [] as [string, string][],
    };
  },
  emits: ['update:rule', 'invalid', 'remove-section'],
  computed: {
    ...mapStores(useDatasetsStore),

    localRule(): GlobalFilterRuleSectionWithIds {
      return _.cloneDeep(this.rule) as GlobalFilterRuleSectionWithIds;
    },

    tableFormattedData(): Entry[] {
      const entries = this.localRule.value.entries as GlobalFilterRuleEntryWithIds[];
      return _.map(entries, (entry) => {
        return {
          id: entry.id,
          category: entry.value[0],
          match: entry.value[1],
          annotation: entry.value[2],
        } as Entry;
      });
    },

    isEntriesEmpty() {
      return !this.localRule?.value?.entries?.length;
    },

    isRecursive() {
      const entries = this.localRule?.value?.entries;
      return !this.isEntriesEmpty && !_.isArray(entries[0].value);
    },

    cardTitle() {
      const length = this.localRule.value.entries.length;
      if (!length) {
        return this.$t('globalFilter.configPage.ruleCard.cardTitleEmpty');
      }
      if (this.isRecursive) {
        return this.$t('globalFilter.configPage.ruleCard.cardTitleSections', { count: length });
      } else {
        return this.$t('globalFilter.configPage.ruleCard.cardTitleEntries', { count: length });
      }
    },
  },
  methods: {
    async loadAsns() {
      this.asns = await this.datasetsStore.getAsnsSortedList();
    },

    async loadCountries() {
      this.countries = await this.datasetsStore.getCountriesSortedList();
    },

    ruleUpdated(val: GlobalFilterRuleSectionWithIds, index: number) {
      this.localRule.value.entries[index] = val;
      this.emitRuleUpdate();
    },

    emitRuleUpdate() {
      this.$emit('update:rule', this.localRule);
    },

    emitValidity(invalid?: boolean) {
      this.$emit('invalid', invalid);
    },

    emitRemoveSection() {
      this.$emit('remove-section', this.localRule);
    },

    removeAllEntries() {
      this.localRule.value.entries = [];
      this.emitRuleUpdate();
    },

    removeSection(sectionIndex: number) {
      this.localRule.value.entries.splice(sectionIndex, 1);
      this.emitRuleUpdate();
    },

    ruleContainsSameCategoryItems() {
      const countedCategories = _.countBy(
        this.localRule.value.entries as GlobalFilterRuleEntryWithIds[],
        (entry) => {
          return entry.value[0];
        }
      );
      const categories = Object.keys(this.listEntryTypes);
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        if (this.isCategorySimpleInput(category as Entry['category']) || category === 'tag') {
          continue;
        }
        if (countedCategories[category] > 1) {
          return true;
        }
      }
      return false;
    },

    toggleRuleRelation() {
      if (this.ruleContainsSameCategoryItems()) {
        return;
      }
      this.localRule.value.relation = this.localRule.value.relation === 'AND' ? 'OR' : 'AND';
      this.emitRuleUpdate();
    },

    addSection() {
      const newSection = {
        relation: 'OR' as Relation,
        entries: [] as GlobalFilterRuleWithIds[],
      };
      if (!this.isRecursive && this.localRule.value.entries.length) {
        const currentSection = {
          relation: this.localRule.value.relation as Relation,
          entries: this.localRule.value.entries as GlobalFilterRuleWithIds[],
        };
        this.localRule.value = {
          relation: 'OR',
          entries: [
            {
              id: DatasetsUtils.generateUUID2(),
              value: currentSection,
            },
          ],
        };
      }
      this.localRule.value.entries.push({
        id: DatasetsUtils.generateUUID2(),
        value: newSection,
      });
      this.emitRuleUpdate();
    },

    isCategorySimpleInput(category: Entry['category']) {
      return new RegExp('(args|cookies|headers|plugins)').test(category);
    },

    removeEntry(entry: GenericObject) {
      this.localRule.value.entries = _.filter(this.localRule.value.entries, (entryToFilter) => {
        return entryToFilter.id !== entry.id;
      });
      this.emitRuleUpdate();
      this.$nextTick(this.validateDuplicates);
    },

    setEditRowEntryData(entry: GenericObject) {
      const category = entry.category;
      if (this.isCategorySimpleInput(category)) {
        if (category.startsWith('plugins')) {
          const splitMatchName = entry.match[0].split('.');
          const pluginType = splitMatchName.shift();
          entry.category = `plugins.${pluginType}` as Entry['category'];
          entry.matchName = splitMatchName.join('.');
          entry.matchValue = entry.match[1];
        } else {
          entry.matchName = entry.match[0];
          entry.matchValue = entry.match[1];
        }
      }
    },

    editEntry(newEntry: GenericObject) {
      const newCategory = newEntry.category;
      const newAnnotation = newEntry.annotation;
      let entryToAdd: GlobalFilterRuleEntry;
      if (this.isCategorySimpleInput(newCategory)) {
        const newEntryName = newEntry.matchName.trim();
        const newEntryValue = newEntry.matchValue.trim();
        if (newEntryName && newEntryValue) {
          if (newCategory.startsWith('plugins')) {
            const pluginType = newCategory.split('.')[1];
            entryToAdd = [
              'plugins',
              [`${pluginType}.${newEntryName}`, newEntryValue],
              newAnnotation,
            ];
          } else {
            entryToAdd = [
              newCategory as EntriesCategory,
              [newEntryName, newEntryValue],
              newAnnotation,
            ];
          }
        }
      } else {
        let [newEntryMatch, newEntryAnnotation] = newEntry.match.trim().split('#');
        newEntryAnnotation = newEntryAnnotation ? newEntryAnnotation.trim() : newAnnotation;
        entryToAdd = [newCategory as EntriesCategory, newEntryMatch, newEntryAnnotation];
      }
      const oldEntryIndex = _.findIndex(this.localRule.value.entries, (entry) => {
        return entry.id === newEntry.id;
      });
      this.localRule.value.entries[oldEntryIndex].value = entryToAdd;
      this.emitRuleUpdate();
      this.cancelEditEntry(newEntry.id);
      this.$nextTick(this.validateDuplicates);
    },

    cancelEditEntry(id: string) {
      delete this.$refs.entriesTable.rowsInEditMode[id];
    },

    addEntry(newEntry: GenericObject) {
      const newCategory = newEntry.category;
      const newAnnotation = newEntry.annotation;
      if (this.isCategorySimpleInput(newCategory)) {
        const newEntryName = newEntry.matchName.trim();
        const newEntryValue = newEntry.matchValue.trim();
        if (newEntryName && newEntryValue) {
          if (newCategory.startsWith('plugins')) {
            const pluginType = newCategory.split('.')[1];
            this.localRule.value.entries.push({
              id: DatasetsUtils.generateUUID2(),
              value: ['plugins', [`${pluginType}.${newEntryName}`, newEntryValue], newAnnotation],
            });
          } else {
            this.localRule.value.entries.push({
              id: DatasetsUtils.generateUUID2(),
              value: [newCategory as EntriesCategory, [newEntryName, newEntryValue], newAnnotation],
            });
          }
        }
      } else {
        const generalAnnotation = newAnnotation;
        _.each(newEntry.match.split('\n'), (line) => {
          let [entry, annotation] = line.trim().split('#');
          entry = entry.trim();
          annotation = annotation ? annotation.trim() : generalAnnotation;
          if (entry) {
            this.localRule.value.entries.push({
              id: DatasetsUtils.generateUUID2(),
              value: [newCategory as EntriesCategory, entry, annotation],
            });
          }
        });
      }
      // change relation to 'OR' if needed
      if (this.ruleContainsSameCategoryItems()) {
        this.localRule.value.relation = 'OR';
      }
      this.emitRuleUpdate();
      this.cancelAddEntry();
      this.$nextTick(this.validateDuplicates);
    },

    cancelAddEntry() {
      this.$refs.entriesTable.setShowNewRow(false);
    },

    dualEntryMatch(entryCategory: Entry['category'], entryMatch: string | string[]) {
      if (_.isArray(entryMatch)) {
        if (entryCategory.startsWith('plugins')) {
          const pluginValue = entryMatch[0].split('.')[1];
          return `${pluginValue}: ${entryMatch[1]}`;
        } else {
          return `${entryMatch[0]}: ${entryMatch[1]}`;
        }
      } else {
        if (entryCategory === 'asn') {
          return `AS${entryMatch}`;
        }
        return entryMatch;
      }
    },

    validateDuplicates() {
      this.duplicatedEntries = [];
      const groupedEntries = _.groupBy(
        this.localRule.value.entries as GlobalFilterRuleEntryWithIds[],
        (entry) => {
          return entry.value[0];
        }
      );
      _.forEach(groupedEntries, (ruleEntries: GlobalFilterRuleEntryWithIds[], key: string) => {
        const category = key as EntriesCategory;
        const values: (string | string[])[] = ruleEntries.map(
          (ruleEntry: GlobalFilterRuleEntryWithIds) => ruleEntry.value[1]
        );
        const duplicateItems: (string | string[])[] = values.filter((filterValue, index) =>
          values.slice(index + 1).some((findValue) => _.isEqual(filterValue, findValue))
        );
        const uniqueDuplicateItems: (string | string[])[] = _.uniqWith(duplicateItems, _.isEqual);
        uniqueDuplicateItems.forEach((value) => {
          this.duplicatedEntries.push([category, value]);
        });
      });
      if (this.duplicatedEntries.length) {
        const duplicatesMsg = this.duplicatedEntries.reduce(
          (prev: string, [category, match]: [EntriesCategory, string | string[]]) => {
            let entryType: string;
            if (category === 'plugins' && Array.isArray(match)) {
              const pluginType = match[0].split('.')[0];
              entryType = this.listEntryTypes[`plugins.${pluginType}`];
            } else {
              entryType = this.listEntryTypes[category];
            }
            const dualEntryMatch = this.dualEntryMatch(category, match);
            return `${prev}\n${entryType} = ${dualEntryMatch}`;
          },
          ''
        );
        this.emitValidity(true);
        Utils.toast({
          message: this.$t('globalFilter.configPage.ruleCard.errors.duplicateEntries', {
            entries: duplicatesMsg,
          }),
          type: 'is-danger',
        });
      } else {
        this.emitValidity(false);
      }
    },

    isEntryDuplicate(entryCategory: Entry['category'], entryMatch: string | string[]): boolean {
      return this.duplicatedEntries.some(([category, value]) => {
        return category === entryCategory && _.isEqual(value, entryMatch);
      });
    },

    resetNewEntryValues(newEntry: GenericObject) {
      newEntry.match = null;
      newEntry.matchName = null;
      newEntry.matchValue = null;
      newEntry.annotation = null;
      this.$nextTick(() => {
        if (this.$refs.newEntryMatchName) {
          this.$refs.newEntryMatchName.setIsDirty(false);
        }
        if (this.$refs.newEntryMatchValue) {
          this.$refs.newEntryMatchValue.setIsDirty(false);
        }
        if (this.$refs.newEntryMatch) {
          this.$refs.newEntryMatch.setIsDirty(false);
        }
        if (this.$refs.newEntryAnnotation) {
          this.$refs.newEntryAnnotation.setIsDirty(false);
        }
      });
    },

    setNewEntryCountryDropdownMatch(rowProps: GenericObject, value: string | string[]) {
      const stringValue = Array.isArray(value) ? value[0] : value;
      rowProps.data.match = stringValue;
      rowProps.data.annotation = ' ';
    },

    setNewEntryAsnDropdownMatch(rowProps: GenericObject, value: string | string[]) {
      const stringValue = Array.isArray(value) ? value[0] : value;
      let name =
        _.find(this.asns, (item) => {
          return item[0] === stringValue;
        })?.[1] ?? '';
      if (name) {
        const splitName = name.split(' ');
        splitName.shift();
        name = splitName.join(' ');
      }
      rowProps.data.match = stringValue;
      rowProps.data.annotation = name;
    },

    validateIP(value: string): string {
      const ipList = value.split('\n').filter((ip) => ip);
      const invalidIPs: string[] = [];
      ipList.forEach((lineValue, index) => {
        lineValue = lineValue.trim();
        const splitValue = lineValue.split('/');
        const isIP = Utils.isIP(splitValue[0]);
        let isCidr = true;
        if (isIP) {
          if (splitValue[1]) {
            const ipv4 = Utils.isIPv4(splitValue[0]);
            try {
              if (ipv4) {
                isCidr =
                  ipaddr.IPv4.networkAddressFromCIDR(lineValue).octets.join('.') === splitValue[0];
              } else {
                const normalizedIP = ipaddr.parse(splitValue[0]).toNormalizedString();
                isCidr =
                  _.map(ipaddr.IPv6.networkAddressFromCIDR(lineValue).parts, (part) => {
                    return part.toString(16);
                  }).join(':') === normalizedIP;
              }
            } catch {
              isCidr = false;
            }
          }
        }
        if (!isIP || !isCidr) {
          const message = this.$t('globalFilter.configPage.ruleCard.errors.invalidIPsEntry', {
            line: index + 1,
            value: lineValue,
          });
          invalidIPs.push(ipList.length > 1 ? message : lineValue);
        }
      });
      if (invalidIPs.length) {
        return invalidIPs.join('\n');
      }
    },

    newEntryMatchNameError(newEntry: GenericObject): string | undefined {
      if (!newEntry.matchName) {
        return this.$t('globalFilter.configPage.ruleCard.errors.emptyMatchName');
      }
    },

    newEntryMatchValueError(newEntry: GenericObject): string | undefined {
      if (!newEntry.matchValue) {
        return this.$t('globalFilter.configPage.ruleCard.errors.emptyMatchValue');
      }
    },

    newEntryMatchError(newEntry: GenericObject): string | undefined {
      if (!newEntry.match) {
        return this.$t('globalFilter.configPage.ruleCard.errors.emptyMatch');
      }
      if (newEntry.category === 'ip') {
        const invalidIPs = this.validateIP(newEntry.match);
        if (invalidIPs) {
          return this.$t('globalFilter.configPage.ruleCard.errors.invalidIPs', {
            entries: invalidIPs,
          });
        }
      }
    },

    newEntryAnnotationError(newEntry: GenericObject): string | undefined {
      if (this.isCategorySimpleInput(newEntry.category)) {
        if (!newEntry.annotation) {
          return this.$t('globalFilter.configPage.ruleCard.errors.emptyAnnotation');
        }
      } else {
        const match = newEntry.match;
        const counter = {
          entries: 0,
          annotations: 0,
        };
        _.forEach(match?.split('\n'), (line) => {
          const [entry, annotation] = line.trim().split('#');
          if (entry?.trim()) {
            counter.entries++;
          }
          if (annotation?.trim()) {
            counter.annotations++;
          }
        });
        if (!newEntry.annotation && counter.entries > counter.annotations) {
          return this.$t('globalFilter.configPage.ruleCard.errors.emptyAnnotation');
        }
      }
    },

    newEntryError(newEntry: GenericObject): string | undefined {
      if (this.isCategorySimpleInput(newEntry.category)) {
        if (this.newEntryMatchNameError(newEntry)) {
          return this.newEntryMatchNameError(newEntry);
        }
        if (this.newEntryMatchValueError(newEntry)) {
          return this.newEntryMatchValueError(newEntry);
        }
      } else {
        if (this.newEntryMatchError(newEntry)) {
          return this.newEntryMatchError(newEntry);
        }
      }
      if (this.newEntryAnnotationError(newEntry)) {
        return this.newEntryAnnotationError(newEntry);
      }
    },
  },
  mounted() {
    this.loadAsns();
    this.loadCountries();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'ui-component-library/assets/styles/main';
@use 'sass:map';

.card-title {
  align-items: center;
  display: flex;
  gap: 16px;
}

.sections-wrapper:not(:last-child) {
  margin-bottom: 24px;
}

.entries-table {
  background-color: transparent;
}

.new-entry-match {
  display: flex;
  gap: 24px;

  :deep(.input-holder) {
    display: flex;
    flex-grow: 1;
  }
}

:deep(.textarea-holder .error-message) {
  bottom: 0;
  position: relative;
  white-space: pre;
}

:deep(.duplicate-entry .data-cell) {
  @extend .rbz-text-danger;
}

:deep(.dropdown-menu) {
  left: 0;
}
</style>
